import type { EventRecurrence, EventRow } from "@rally/agreed";
import {
  Button,
  Checkbox,
  ChoiceChips,
  Col,
  DateStepper,
  Field,
  Input,
  Row,
  Select,
  SidePanel,
  Text,
  TimePicker,
  useConfirm,
  useTheme,
} from "@rally/ui-kit";
import { useEffect, useState } from "react";

import { say } from "../localization/say.ts";
import { act, read } from "../net/rally.ts";
import { DAY } from "./calendar-time.ts";

const RECURRENCES = ["none", "daily", "weekly", "monthly", "yearly"] as const;
const LENGTHS = [30, 60, 90, 120] as const;

function dayOf(at: Date): Date {
  return new Date(at.getFullYear(), at.getMonth(), at.getDate());
}

function clockOf(at: Date): string {
  const snapped = Math.round(at.getMinutes() / 15) * 15;
  return `${String(at.getHours()).padStart(2, "0")}:${String(Math.min(45, snapped)).padStart(2, "0")}`;
}

function joined(day: Date, clock: string): Date {
  const [hour, minute] = clock.split(":").map(Number);
  return new Date(day.getFullYear(), day.getMonth(), day.getDate(), hour ?? 0, minute ?? 0);
}

function lengthSaid(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  return minutes % 60 === 0 ? `${minutes / 60}h` : `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

export function EventPanel({
  at,
  until,
  held,
  onDone,
  onClose,
}: {
  at: Date;
  until?: Date;
  held: EventRow | null;
  onDone: () => void;
  onClose: () => void;
}) {
  const _theme = useTheme();
  const opening = held ? new Date(held.startsAt) : at;
  const [title, setTitle] = useState(held?.title ?? "");
  const [day, setDay] = useState(() => dayOf(opening));
  const [clock, setClock] = useState(() => clockOf(opening));
  const [minutes, setMinutes] = useState(() =>
    held?.endsAt
      ? Math.max(15, (new Date(held.endsAt).getTime() - new Date(held.startsAt).getTime()) / 60000)
      : until
        ? Math.max(15, (until.getTime() - at.getTime()) / 60000)
        : 60,
  );
  const [recurrence, setRecurrence] = useState<EventRecurrence>(held?.recurrence ?? "none");
  const { ask, dialog } = useConfirm();
  const [untilDay, setUntilDay] = useState<Date | null>(
    held?.recurrenceEnd ? dayOf(new Date(held.recurrenceEnd)) : null,
  );
  const [failure, setFailure] = useState("");
  const [busy, setBusy] = useState(false);
  const [conversation, setConversation] = useState<string>(held?.conversation ?? "");
  const [allDay, setAllDay] = useState(held?.allDay ?? false);
  const [rooms, setRooms] = useState<Array<{ value: string; label: string }>>([]);
  useEffect(() => {
    let alive = true;
    void read<{
      projects: Array<{ name: string; conversations: Array<{ id: string; title: string | null }> }>;
      personal: Array<{ id: string; title: string | null }>;
    }>("where.i.am", {})
      .then((where) => {
        if (!alive) return;
        const named = (one: { id: string; title: string | null }, under?: string) => ({
          value: one.id,
          label: `${under ? `${under} · ` : ""}${one.title ?? say("chat.unnamed")}`,
        });
        setRooms([
          ...where.personal.map((one) => named(one)),
          ...where.projects.flatMap((project) =>
            project.conversations.map((one) => named(one, project.name)),
          ),
        ]);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);
  const repeat = recurrence !== "none";

  async function send() {
    const startsAt = allDay ? dayOf(day) : joined(day, clock);
    const endsAt = allDay
      ? new Date(startsAt.getTime() + DAY)
      : new Date(startsAt.getTime() + minutes * 60000);
    setBusy(true);
    setFailure("");
    const untilIso = repeat ? (untilDay ?? new Date(startsAt.getTime() + 30 * DAY)).toISOString() : null;
    try {
      if (held) {
        await act("event.update", {
          event: held.id,
          title: title.trim(),
          startsAt: startsAt.toISOString(),
          endsAt: endsAt.toISOString(),
          recurrence,
          recurrenceEnd: untilIso,
          conversation: conversation || null,
          allDay,
        });
      } else {
        await act("event.create", {
          title: title.trim(),
          startsAt: startsAt.toISOString(),
          endsAt: endsAt.toISOString(),
          recurrence,
          ...(repeat ? { recurrenceEnd: untilIso } : {}),
          ...(conversation ? { conversation } : {}),
          allDay,
        });
      }
      onDone();
    } catch {
      setFailure(say("calendar.refused"));
      setBusy(false);
    }
  }

  return (
    <>
      {dialog}
      <SidePanel
        title={held ? say("calendar.event.edit") : say("calendar.new")}
        visible
        width={420}
        onClose={onClose}
        testID="calendar-form"
        footer={
          <>
            {held ? (
              <Button
                variant="ghost"
                icon="trash"
                accessibilityLabel={say("calendar.delete")}
                onPress={() => {
                  void ask({
                    title: say("calendar.deleteTitle"),
                    description: say("calendar.deleteMeans"),
                    confirmLabel: say("calendar.delete"),
                    cancelLabel: say("calendar.keep"),
                    tone: "danger",
                  }).then((sure) => {
                    if (!sure) return;
                    void act("event.delete", { event: held.id })
                      .then(onDone)
                      .catch(() => setFailure(say("calendar.refused")));
                  });
                }}
              />
            ) : null}
            <Col style={{ flex: 1 }} />
            <Button variant="ghost" onPress={onClose}>
              {say("calendar.cancel")}
            </Button>
            <Button disabled={!title.trim()} loading={busy} onPress={() => void send()}>
              {held ? say("calendar.save") : say("calendar.create")}
            </Button>
          </>
        }
      >
        <Field label={say("calendar.field.title")}>
          <Input
            value={title}
            onChangeText={setTitle}
            placeholder={say("calendar.titlePlaceholder")}
            accessibilityLabel={say("calendar.field.title")}
            testID="event-title"
          />
        </Field>

        <Field label={say("calendar.field.starts")}>
          <Row gap={2} align="center">
            <Col style={{ flex: 1 }}>
              <DateStepper
                value={day}
                onChange={setDay}
                previousLabel={say("calendar.previous")}
                nextLabel={say("calendar.next")}
              />
            </Col>
            {allDay ? null : (
              <Col style={{ width: 116 }}>
                <TimePicker value={clock} onChange={setClock} />
              </Col>
            )}
          </Row>
        </Field>

        <Checkbox
          checked={allDay}
          onChange={setAllDay}
          label={say("calendar.field.allDay")}
          testID="event-all-day"
        />

        {allDay ? null : (
          <Field label={say("calendar.field.length")}>
            <ChoiceChips
              value={minutes}
              onChange={setMinutes}
              options={LENGTHS.map((one) => ({
                value: one,
                label: lengthSaid(one),
                testID: `event-length-${one}`,
              }))}
            />
          </Field>
        )}

        {rooms.length > 0 ? (
          <Field label={say("calendar.field.conversation")}>
            <Select
              value={conversation}
              onChange={setConversation}
              placeholder={say("calendar.noConversation")}
              options={[{ value: "", label: say("calendar.noConversation") }, ...rooms]}
            />
          </Field>
        ) : null}

        <Field label={say("calendar.field.repeats")}>
          <Select
            value={recurrence}
            onChange={(value) => setRecurrence(value as EventRecurrence)}
            options={RECURRENCES.map((one) => ({ value: one, label: say(`calendar.recurrence.${one}`) }))}
          />
        </Field>

        {repeat ? (
          <Field label={say("calendar.field.until")}>
            <DateStepper
              value={untilDay ?? new Date(day.getTime() + 30 * DAY)}
              onChange={setUntilDay}
              stepDays={1}
              said={{ day: "numeric", month: "short", year: "numeric" }}
              previousLabel={say("calendar.previous")}
              nextLabel={say("calendar.next")}
            />
          </Field>
        ) : null}

        {failure ? (
          <Text typeName="caption" color="danger" accessibilityLiveRegion="polite">
            {failure}
          </Text>
        ) : null}
      </SidePanel>
    </>
  );
}
